// Unpublished Work © 2025-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {DndContext, useSensor, useSensors, TouchSensor, MouseSensor} from '@dnd-kit/core';
import {SortableContext} from '@dnd-kit/sortable';
import {restrictToParentElement, restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {Box, Typography, IconButton, Dialog, Button, Stack} from '@deere/isg.component-library';
import {IconAdd, IconRemove} from '@deere/icons';
import AssignDetail from './assign-detail';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {deleteSelectedOperators, removeSelectedJobs} from 'Src/ui-core/store/actions/setup-workboard';
import {connect} from 'react-redux';
import {GOLF_FLEET_TO_EQUIPMENT, PRO_GOLF_NOVEMBER_2026_RELEASE} from 'Common/constants/isg-toggles';

const ICON_REMOVE_STYLE = {
    primary: '#333',
    style: {
        height: '24px',
        width: '24px'
    }
};

function renderAssignmentDetails(
    equipment,
    equipmentInUse,
    equipmentLoading,
    selectedJobs,
    selectedWorkItems,
    selectedWorkItemsIds,
    buildWorkItemKey,
    groupBy,
    handleDragEnd,
    updateWorkItem,
    fleetId,
    translations,
    isMobile,
    isFleetToEquipmentEnabled
) {
    const hasItems = selectedWorkItems.length > 0;
    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5
            }
        })
    );

    return (
        <>
            {hasItems ? (
                <DndContext
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    onDragEnd={handleDragEnd}
                    sensors={isMobile ? sensors : undefined}
                >
                    <SortableContext items={selectedWorkItemsIds}>
                        <Box>
                            {selectedWorkItems.map((workItem) => {
                                const key = buildWorkItemKey(workItem.jobId, workItem.operatorId);

                                return (
                                    <AssignDetail
                                        equipment={equipment}
                                        equipmentInUse={equipmentInUse}
                                        equipmentLoading={equipmentLoading}
                                        fleetId={fleetId}
                                        groupBy={groupBy}
                                        isFleetToEquipmentEnabled={isFleetToEquipmentEnabled}
                                        jobs={selectedJobs}
                                        key={key}
                                        translations={translations}
                                        updateWorkItem={(values) => updateWorkItem({
                                            key,
                                            values
                                        })}
                                        workItem={workItem}
                                    />
                                );
                            })}
                        </Box>
                    </SortableContext>
                </DndContext>
            ) : (
                <AssignDetail
                    groupBy={groupBy}
                    translations={translations}
                />
            )}
        </>
    );
}

function AssignDetailWrapper(props) {
    const {
        isMobile,
        onDialogConfirm,
        openAddAssignmentsDialog,
        handleAddedItems,
        isOpen,
        equipment = [],
        equipmentInUse,
        equipmentLoading,
        buildWorkItemKey,
        title,
        closeHandler,
        translations,
        handleDragEnd,
        groupBy,
        selectedWorkItems,
        selectedWorkItemsIds,
        updateWorkItem,
        selectedOperators,
        selectedJobs,
        handleRemoveSelectedJob,
        handleRemoveSelectedOperator,
        fleetId,
        isgToggles
    } = props;

    const isNovReleaseEnabled = isgToggles?.[PRO_GOLF_NOVEMBER_2026_RELEASE];

    const [itemsAdded, setItemsAdded] = React.useState([]);

    const cancelChanges = React.useCallback(() => {
        if (groupBy === JOB) {
            handleRemoveSelectedOperator({
                idsToRemove: [...itemsAdded],
                operators: [...selectedOperators]
            });
        } else {
            handleRemoveSelectedJob({
                idsToRemove: [...itemsAdded],
                jobs: [...selectedJobs]
            });
        }

        closeHandler(itemsAdded);
        setItemsAdded([]);
    }, [itemsAdded, groupBy, selectedOperators, selectedJobs, handleRemoveSelectedJob, handleRemoveSelectedOperator]);

    const footerActions = React.useMemo(() => {
        return [{
            title: translations.CANCEL,
            variant: 'secondary',
            onClick: cancelChanges
        }, {
            title: translations.DONE,
            variant: 'secondary',
            onClick: () => {
                setItemsAdded([]);
                onDialogConfirm();
            }
        }];
    }, [closeHandler]);

    const assignmentDetailsEl = renderAssignmentDetails(
        equipment,
        equipmentInUse,
        equipmentLoading,
        selectedJobs,
        selectedWorkItems,
        selectedWorkItemsIds,
        buildWorkItemKey,
        groupBy,
        handleDragEnd,
        updateWorkItem,
        fleetId,
        translations,
        isMobile,
        Boolean(isgToggles?.[GOLF_FLEET_TO_EQUIPMENT])
    );

    return (
        <>
            {!isMobile && isOpen && <Box
                sx={{
                    flex: '0 0 25%',
                    border: '1px solid #C7C6C5',
                    ...isNovReleaseEnabled ? {
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                        maxHeight: 'calc(100dvh - 416px)',
                        m: 'calc(1rem + 16px) 1rem 5rem 0',
                        ...!isMobile && {
                            borderRadius: '4px'
                        }
                    } : {
                        height: '100%',
                        m: '1rem 1rem 1rem 0',
                        mb: '5rem'
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: 'white',
                        p: '16px',
                        ...isNovReleaseEnabled && {
                            flexShrink: 0,
                            borderBottom: '1px solid #C7C6C5',
                            ...!isMobile && {
                                borderTopLeftRadius: '4px',
                                borderTopRightRadius: '4px'
                            }
                        }
                    }}
                >
                    <Typography variant='h2'>{title}</Typography>
                    <IconButton
                        onClick={closeHandler}
                        variant='borderless'
                    >
                        <IconRemove {...ICON_REMOVE_STYLE}/>
                    </IconButton>
                </Box>
                <Box
                    sx={isNovReleaseEnabled && {
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto'
                    }}
                >
                    {assignmentDetailsEl}
                </Box>
            </Box>}
            <Dialog
                className='gray-bg-dialog'
                closeHandler={cancelChanges}
                footerActions={footerActions}
                open={isOpen && isMobile}
                title={title}
            >
                {assignmentDetailsEl}
                <Stack
                    alignItems={'flex-start'}
                    marginY={'1rem'}
                >
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            openAddAssignmentsDialog({
                                addUser: groupBy === JOB,
                                handleAddedItems: (ids) => {
                                    handleAddedItems(ids);
                                    setItemsAdded((prev) => [...prev, ...ids]);
                                }
                            });
                        }}
                        startIcon={<IconAdd/>}
                        variant='borderless'
                    >
                        {replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                            '0': groupBy === JOB ? translations.OPERATORS : translations.JOBS
                        })}
                    </Button>
                </Stack>
            </Dialog>
        </>
    );
}

AssignDetailWrapper.propTypes = {
    buildWorkItemKey: PropTypes.func,
    closeHandler: PropTypes.func,
    equipment: PropTypes.arrayOf(PropTypes.object),
    equipmentInUse: PropTypes.array,
    equipmentLoading: PropTypes.bool,
    fleetId: PropTypes.string,
    groupBy: PropTypes.string,
    handleAddedItems: PropTypes.func,
    handleDragEnd: PropTypes.func,
    handleRemoveSelectedJob: PropTypes.func,
    handleRemoveSelectedOperator: PropTypes.func,
    isgToggles: PropTypes.object,
    isMobile: PropTypes.bool,
    isOpen: PropTypes.bool,
    onDialogConfirm: PropTypes.func,
    openAddAssignmentsDialog: PropTypes.func,
    selectedJobs: PropTypes.arrayOf(PropTypes.object),
    selectedOperators: PropTypes.arrayOf(PropTypes.object),
    selectedWorkItems: PropTypes.arrayOf(PropTypes.object),
    selectedWorkItemsIds: PropTypes.array,
    title: PropTypes.string,
    translations: PropTypes.translations,
    updateWorkItem: PropTypes.func
};

export function mapStateToProps(state) {
    return {
        equipment: state.setupWorkboard.fleetAndEquipment.equipment,
        equipmentLoading: state.setupWorkboard.fleetAndEquipment.loading,
        fleetId: state.setupWorkboard.fleetAndEquipment.fleetId,
        isgToggles: state.account.isgToggles,
        selectedOperators: state.setupWorkboard.operators.selected,
        selectedJobs: state.setupWorkboard.jobs.selectedJobs,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        handleRemoveSelectedJob(props) {
            dispatch(removeSelectedJobs(props));
        },
        handleRemoveSelectedOperator(props) {
            dispatch(deleteSelectedOperators(props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(AssignDetailWrapper);
